import { App, Form } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton, CustomInput, CustomSelect } from "sipli-fleet-core/components";
import { useMutationUpdateDeviceDetails } from "src/queries/deviceDetails.queries";
import type { DeviceDetail } from "src/services/deviceDetailsService";
import { getApiErrorMessage } from "src/util/apiError";
import DetailsCard, { DetailsReadOnlyField } from "./DetailsCard";
import { haveFormValuesChanged } from "./details.helpers";
import { useEpochFormatter } from "../../../../../../sipli-fleet-core/components/EpochDate/useEpochFormatter";

interface DeviceFormValues {
  firmware: string;
  firmwareRevision: string;
  simStatus: number;
  apn: string;
  apnPassword: string;
}

const getDeviceFormValues = (device: DeviceDetail): DeviceFormValues => ({
  firmware: device.firmware ?? "",
  firmwareRevision: device.firmwareRevision ?? "",
  simStatus: device.simStatus ? 1 : 0,
  apn: device.apn ?? "",
  apnPassword: device.apnPassword ?? "",
});

const DeviceDetailsCard = ({ device }: { device: DeviceDetail }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<DeviceFormValues>();
  const [initialValues, setInitialValues] = useState(() => getDeviceFormValues(device));
  const mutation = useMutationUpdateDeviceDetails();

  const formatEpoch = useEpochFormatter();

  useEffect(() => {
    const values = getDeviceFormValues(device);
    setInitialValues(values);
    form.resetFields();
    form.setFieldsValue(values);
  }, [device, form]);

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(initialValues);
  };

  const handleSave = async () => {
    if (device.id === undefined) return;
    const values = await form.validateFields();
    try {
      await mutation.mutateAsync({
        id: device.id,
        payload: {
          companyId: "",
          firmware: values.firmware ?? "",
          firmwareRevision: values.firmwareRevision ?? "",
          // simStatus: values.simStatus === 1,
          apn: values.apn ?? "",
          apnPassword: values.apnPassword ?? "",
        },
      });
      setInitialValues(values);
      message.success(t("device_detail_updated_successfully"));
    } catch (error) {
      message.error(`Error: ${getApiErrorMessage(error) ?? t("device_detail_update_failed")}`);
    }
  };

  return (
    <DetailsCard
      title={t("device_details")}
      extra={device.unit?.serial || device.unit?.imei || "--"}
    >
      <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 xl:grid-cols-4">
          <DetailsReadOnlyField label={t("serial_number")} value={device.unit?.serial} />
          <DetailsReadOnlyField label={t("imei")} value={device.unit?.imei} />
          <DetailsReadOnlyField label={t("telephone")} value={device.unit?.phoneNumber} />
          <DetailsReadOnlyField
            label={t("sim_status")}
            value={
              device.unit?.simStatus == null
                ? "--"
                : device.unit.simStatus
                  ? t("active")
                  : t("inactive")
            }
          />

          {/* <Form.Item name="simStatus" label={t("sim_status")}>
            <CustomSelect
              className="common-select"
              placeholder={t("sim_status")}
              options={[
                { value: 1, label: t("active") },
                { value: 0, label: t("inactive") },
              ]}
            />
          </Form.Item> */}
          <DetailsReadOnlyField
            label={t("device_type")}
            value={[device.unit?.make?.name, device.unit?.model?.name].filter(Boolean).join(" ")}
            className="pb-4 xl:col-span-2"
          />
        </div>
        <div className="mb-6 flex flex-wrap gap-4 border-y border-gray4 py-4 pb-2">
          <DetailsReadOnlyField
            label={t("created_at")}
            value={formatEpoch(device.createDate)}
            className="w-[190px] max-w-full"
          />
          <DetailsReadOnlyField
            label={t("installed_on")}
            value={formatEpoch(device.installedAt)}
            className="w-[190px] max-w-full"
          />
        </div>
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 xl:grid-cols-3">
          <Form.Item name="firmware" label={t("firmware")}>
            <CustomInput className="common-input" placeholder={t("firmware")} />
          </Form.Item>
          <Form.Item name="firmwareRevision" label={t("revision_firmware")}>
            <CustomInput className="common-input" placeholder={t("revision_firmware")} />
          </Form.Item>
          <DetailsReadOnlyField label={t("show_off_after_min")} value={device.showOffAfter} />
          <DetailsReadOnlyField label="IMSI" value={device.imsi} />
          <Form.Item name="apn" label="APN">
            <CustomInput className="common-input" placeholder="APN" />
          </Form.Item>
          <Form.Item name="apnPassword" label={t("apn_password")}>
            <CustomInput className="common-input" placeholder={t("apn_password")} />
          </Form.Item>
        </div>
        <div className="flex justify-end pt-8">
          <div className="flex gap-2">
            <CustomButton
              type="secondary"
              label={t("cancel")}
              className="!h-[32px] !w-[119px] !rounded-[4px] !text-[14px] !font-semibold !leading-[18px] !tracking-[0.05px]"
              disabled={mutation.isPending}
              onClick={handleCancel}
            />
            <Form.Item noStyle shouldUpdate>
              {() => (
                <CustomButton
                  htmlType="submit"
                  type="primary"
                  label={t("save")}
                  className="!h-[32px] !w-[119px] !rounded-[4px] !text-[14px] !font-semibold !leading-[18px] !tracking-[0.05px] !text-gray2"
                  loading={mutation.isPending}
                  disabled={
                    !haveFormValuesChanged(initialValues, form.getFieldsValue(true)) ||
                    mutation.isPending
                  }
                />
              )}
            </Form.Item>
          </div>
        </div>
      </Form>
    </DetailsCard>
  );
};

export default DeviceDetailsCard;
